'use client'

import React, { createContext, ReactNode, useContext, useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@rewardops/ui'

interface ErrorModalContextType {
  showError: (
    Icon: React.ComponentType<any>,
    title: string,
    message: string,
  ) => void
}

const ErrorModalContext = createContext<ErrorModalContextType | undefined>(
  undefined,
)

export const useErrorModal = () => {
  const context = useContext(ErrorModalContext)
  if (!context) {
    throw new Error('useErrorModal must be used within an ErrorModalProvider')
  }
  return context
}

interface ErrorModalProviderProps {
  children: ReactNode
}

export const ErrorModalProvider: React.FC<ErrorModalProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [details, setDetails] = useState<{
    Icon: React.ComponentType<any> | null
    title: string
    message: string
  }>({ Icon: null, title: '', message: '' })

  const showError = (
    Icon: React.ComponentType<any>,
    title: string,
    message: string,
  ) => {
    setDetails({ Icon, title, message })
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <ErrorModalContext.Provider value={{ showError }}>
      {children}
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="relative p-6">
            <div className="flex flex-col items-center text-center">
              {details.Icon && (
                <div className="m-8">
                  <details.Icon className="h-16 w-16" />
                </div>
              )}
              <h3 className="mb-4 text-2xl font-semibold">{details.title}</h3>
              <p className="mb-6 text-gray-600">
                {details.message.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ErrorModalContext.Provider>
  )
}
